import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Input, Button, message, Divider } from 'antd';
import { GoogleOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { handleApiError } from '../utils/errorHandler';
import { useLanguage } from '../contexts/LanguageContext';
import en from '../locales/en';
import zh from '../locales/zh';

const Container = styled.div`
  min-height: calc(100vh - 64px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  
  @media (max-width: 768px) {
    padding: 16px;
    min-height: calc(100vh - 56px); /* 移动端导航栏通常更小 */
  }
`;

const FormCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 40px;
  border-radius: 16px;
  width: 100%;
  max-width: 400px;
  backdrop-filter: blur(10px);
  
  @media (max-width: 768px) {
    padding: 24px;
    max-width: 100%;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
    border-radius: 12px;
  }
`;

const Title = styled.h1`
  color: #fff;
  text-align: center;
  margin-bottom: 24px;
  font-size: 28px;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const StyledInput = styled(Input)`
  background: rgba(255, 255, 255, 0.1);
  border: none;
  height: 40px;
  color: #fff !important;
  font-size: 16px;
  
  @media (max-width: 480px) {
    height: 44px; /* 移动端增加点击区域 */
    font-size: 16px; /* 确保移动端文字大小合适 */
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus,
  &:hover {
    background: rgba(255, 255, 255, 0.15) !important;
  }

  input {
    background: transparent !important;
    color: #fff !important;
    
    &::placeholder {
      color: rgba(255, 255, 255, 0.5);
    }
  }
`;

const StyledPassword = styled(Input.Password)`
  &.ant-input-affix-wrapper {
    background: rgba(255, 255, 255, 0.1) !important;
    border: none !important;
    height: 40px;
    color: #fff !important;
    padding: 0 11px;
    
    @media (max-width: 480px) {
      height: 44px;
      font-size: 16px;
    }
    
    &:hover {
      background: rgba(255, 255, 255, 0.15) !important;
    }
    
    &:focus,
    &-focused {
      background: rgba(255, 255, 255, 0.15) !important;
      box-shadow: none !important;
    }
    
    .ant-input {
      background: transparent !important;
      color: #fff !important;
      height: 100%;
      
      &::placeholder {
        color: rgba(255, 255, 255, 0.5);
      }
    }
    
    .ant-input-password-icon {
      color: rgba(255, 255, 255, 0.5);
      font-size: 16px;
      
      @media (max-width: 480px) {
        font-size: 18px; /* 增加图标大小以提高可点击性 */
      }
      
      &:hover {
        color: rgba(255, 255, 255, 0.8);
      }
    }
  }
`;

const Links = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 8px;
  
  @media (max-width: 480px) {
    margin-top: 12px;
  }
`;

const StyledLink = styled(Link)`
  color: #888;
  font-size: 14px;
  padding: 4px 0;
  
  @media (max-width: 480px) {
    font-size: 15px;
    padding: 6px 0;
  }
  
  &:hover {
    color: #fff;
  }
`;

const GoogleButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background-color: #4285F4 !important;
  color: white !important;
  border: none !important;
  height: 40px;
  font-size: 16px;
  
  @media (max-width: 480px) {
    height: 44px;
    font-size: 16px;
  }
  
  &:hover, &:focus {
    background-color: #357ae8 !important;
  }
  
  .anticon {
    font-size: 18px;
    
    @media (max-width: 480px) {
      font-size: 20px;
    }
  }
`;

const OrDivider = styled(Divider)`
  &.ant-divider {
    color: rgba(255, 255, 255, 0.5);
    border-color: rgba(255, 255, 255, 0.2);
    margin: 8px 0;
    
    &::before, &::after {
      border-color: rgba(255, 255, 255, 0.2) !important;
    }
    
    @media (max-width: 480px) {
      margin: 12px 0;
    }
  }
`;

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [googleScriptLoaded, setGoogleScriptLoaded] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentLanguage } = useLanguage();
  const t = currentLanguage === 'en' ? en : zh;

  const from = location.state?.from?.pathname || '/';
  
  // Google 客户端 ID
  const GOOGLE_CLIENT_ID = '781173809517-4ni2ko3tli4tspi3t119ru3g529nhpq5.apps.googleusercontent.com';

  // 加载 Google 登录 SDK
  useEffect(() => {
    const loadGoogleScript = () => {
      // 如果已经加载过，不再重复加载
      if (document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
        console.log('Google Identity Services 已加载');
        setGoogleScriptLoaded(true);
        return;
      }
      
      console.log('开始加载 Google Identity Services');
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.id = 'google-gsi-script';
      
      script.onload = () => {
        console.log('Google Identity Services 加载成功');
        setGoogleScriptLoaded(true);
      };
      
      script.onerror = (error) => {
        console.error('Google Identity Services 加载失败:', error);
      };
      
      document.body.appendChild(script);
    };
    
    loadGoogleScript();
  }, []);

  // 使用 Google 重定向方式登录
  const handleGoogleLogin = () => {
    if (!googleScriptLoaded) {
      message.error('Google 登录服务正在加载，请稍后重试');
      return;
    }
    
    setGoogleLoading(true);
    
    try {
      console.log('准备 Google 登录重定向');
      
      // 创建一个隐藏的表单来执行重定向
      const form = document.createElement('form');
      form.method = 'GET';
      form.action = 'https://accounts.google.com/o/oauth2/v2/auth';
      form.target = '_self';
      
      const redirectUri = window.location.origin + '/auth/google-login';
      // 添加必要的参数
      const params = {
        client_id: GOOGLE_CLIENT_ID,
        redirect_uri: redirectUri, // 使用当前域名而不是 API 服务器
        response_type: 'code',  // 这是固定的，表示返回授权码
        scope: 'email profile',
        access_type: 'online',
        state: JSON.stringify({ 
          from,
          redirect_uri: redirectUri,
          paramName: 'code' // 指定我们希望在回调中使用的参数名
        }),
        prompt: 'select_account'
      };
      
      // 添加所有参数到表单
      Object.keys(params).forEach(key => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = params[key];
        form.appendChild(input);
      });
      
      // 添加表单到文档并提交
      document.body.appendChild(form);
      form.submit();
      
      // 清理表单
      setTimeout(() => {
        document.body.removeChild(form);
      }, 100);
      
    } catch (error) {
      console.error('触发 Google 登录失败:', error);
      message.error('Google 登录失败，请稍后重试');
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      console.log('尝试登录:', { email });
      
      const response = await fetch('https://api.kalimbaflows.com/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      console.log('登录响应状态:', response.status);
      
      // 先获取响应数据
      const data = await response.json();
      console.log('登录响应数据:', data);

      // 然后检查响应状态
      if (!response.ok) {
        const error = new Error(data.message || '登录失败，请检查邮箱和密码');
        // 使用 handleApiError 处理错误
        const errorMsg = handleApiError(error, '登录失败，请检查邮箱和密码');
        setErrorMessage(errorMsg);
        setLoading(false);
        return;
      }

      // 登录成功
      login(data.token);
      message.success('登录成功');
      navigate(from, { replace: true });
    } catch (error) {
      // 捕获所有其他错误，也使用 handleApiError 处理
      const errorMsg = handleApiError(error, '登录失败: 请检查网络连接');
      setErrorMessage(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <FormCard>
        <Title>{t.auth.login.title}</Title>
        <Form onSubmit={handleSubmit}>
          <GoogleButton 
            icon={<GoogleOutlined />} 
            onClick={handleGoogleLogin}
            loading={googleLoading}
            block
          >
            {t.auth.login.googleLogin}
          </GoogleButton>
          
          <OrDivider>or</OrDivider>
          
          <StyledInput
            placeholder={t.auth.login.emailPlaceholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <StyledPassword
            placeholder={t.auth.login.passwordPlaceholder}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
          {errorMessage && (
            <div style={{ color: '#ff4d4f', marginBottom: '8px', textAlign: 'center' }}>
              {errorMessage}
            </div>
          )}
          <Button type="primary" htmlType="submit" loading={loading}>
            {t.auth.login.loginButton}
          </Button>
          <Links>
            <StyledLink to="/register">{t.auth.login.register}</StyledLink>
            <StyledLink to="/reset-password">{t.auth.login.forgotPassword}</StyledLink>
          </Links>
        </Form>
      </FormCard>
    </Container>
  );
}

export default Login;